/**
 * Live discovery-surface fetch helper. Moved unchanged from the original
 * monolithic `Simulation.tsx` per ADR-0159 §6 module split.
 *
 * Fetches a public discovery surface (agent card / ARD catalog) from the
 * browser. These edge GETs are CORS-open (`access-control-allow-origin: *`),
 * so a cross-origin fetch from app.sill.so succeeds. Read-only, no creds.
 * Times the round-trip and pretty-trims the body for display.
 */

/** Result of a real fetch against a live discovery surface. */
export interface LiveResult {
  status: number;
  ms: number;
  /** Pretty-printed for DISPLAY — capped at 900 chars. Never parse this. */
  body: string;
  ok: boolean;
  /**
   * The response parsed from the FULL text, BEFORE the display cap.
   * Fact derivation (`lib/facts.ts`, the Claude skill gate) reads this —
   * a real signed card is longer than 900 chars, so parsing the display
   * `body` silently loses the `skills[]` array (2026-07-05 field bug:
   * a merchant with every skill toggled off saw no Skills fact because
   * the truncated JSON failed to parse). `undefined` = non-JSON.
   */
  json?: unknown;
}

/** Live discovery state for the current run (real fetches). */
export interface LiveData {
  card?: LiveResult;
  catalog?: LiveResult;
  /**
   * MCP `tools/list` response — populated on the Claude journey only,
   * gated by the one-shot flag in `Simulation.tsx`. Absent (undefined)
   * means the phase has not been fired yet OR the current agent's
   * protocol is not `'mcp'`.
   */
  mcp?: LiveResult;
}

export async function fetchSurface(url: string): Promise<LiveResult> {
  const t0 =
    typeof performance !== 'undefined' ? performance.now() : Date.now();
  try {
    const res = await fetch(url, { method: 'GET' });
    const text = await res.text();
    const ms = Math.round(
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0,
    );
    let body = text;
    let json: unknown;
    try {
      json = JSON.parse(text);
      body = JSON.stringify(json, null, 2);
    } catch {
      /* non-JSON or partial — show raw */
    }
    if (body.length > 900) body = `${body.slice(0, 900)}\n… (truncated)`;
    return {
      status: res.status,
      ms,
      body,
      ok: res.ok,
      ...(json !== undefined ? { json } : {}),
    };
  } catch (err) {
    const ms = Math.round(
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0,
    );
    return {
      status: 0,
      ms,
      body: `(couldn't reach the surface: ${
        err instanceof Error ? err.message : 'network error'
      })`,
      ok: false,
    };
  }
}
